import { exists } from "shared/lib/exists"
import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { ProductPosition } from "shared/types/ProductPosition"

import { useUpdateProductPosition } from "entities/product-positions/api/useUpdateProductPosition"
import { useProductPositionMultiActionHandlers } from "entities/product-positions/model/useProductPositionMultiActionHandlers"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { useProjectRequestMonitor } from "entities/projects/model/useProjectRequestState"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { MultiEditProductPositionFormData } from "widgets/multi-edit-product-position-form/MultiEditProductPositionForm"

import { ProductPositionMultiSelectSidebarModel } from "../ProductPositionMultiSelectSidebar"

export interface UseProductPositionMultiSelectSidebarModelArgs {
  projectID: string
  productPositions: ProductPosition[]
}

export function useProductPositionMultiSelectSidebarModel({
  projectID,
  productPositions
}: UseProductPositionMultiSelectSidebarModelArgs): ProductPositionMultiSelectSidebarModel {
  const [updateProductPosition] = useUpdateProductPosition()
  const { isUpdatingChildren } = useProjectRequestMonitor({ projectID })
  const { permissions } = useProjectPermissions({ projectID })

  const handlers = useProductPositionMultiActionHandlers({
    projectID,
    productPositions,
    permissions: permissions ?? readonlyProjectPermissions
  })

  const handleSubmit = useEvent(
    async (
      formData: MultiEditProductPositionFormData
    ): Promise<ApiResponse<unknown>> => {
      if (!exists(formData.productPosition.quantity)) {
        return { data: {}, errors: undefined }
      }
      const results = await Promise.all(
        productPositions.map(productPosition =>
          updateProductPosition({
            projectID,
            productPosition: {
              id: productPosition.id,
              quantity: formData.productPosition.quantity as number
            }
          })
        )
      )
      const errors = results.flatMap(r => r?.errors ?? [])
      return { data: {}, errors: errors.length ? errors : undefined }
    }
  )

  return {
    projectID,
    productPositions,
    callbacks: handlers,
    isDisabled: isUpdatingChildren,
    onSubmit: handleSubmit,
    hideControls: !handlers.onDelete
  }
}
